import { useSharedKpiFilters } from "@/lib/kpiFilterMemory";
import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toParams } from "@/lib/kpi";
import UnifiedFilters from "@/components/UnifiedFilters";
import {
  Trophy, TrendingUp, TrendingDown, Minus, RotateCcw, Download, ChevronDown,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Award, Satellite, Plus, Pencil, Trash2, X,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import CustomDates from "@/components/filters/CustomDates";

const MONTHS_FR = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];
const fmtEur = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ")+" €";
const fmtInt = v => (v==null||isNaN(v))?"—":Math.round(v).toLocaleString("fr-FR").replace(/,/g," ");
const fmtPct = (v,d=1) => (v==null||isNaN(v))?"—":`${v>=0?"+":""}${Number(v).toFixed(d).replace(".",",")}%`;

const SCOPES = [
  { key: "corner",  label: "Corners" },
  { key: "bm",      label: "BM" },
  { key: "dr",      label: "DR" },
  { key: "secteur", label: "Secteur" },
];

const METRICS = [
  { key: "ca_day",           label: "CA Jour" },
  { key: "ca_month",         label: "CA Mensuel" },
  { key: "ca_year",          label: "CA Cumulé" },
  { key: "perf_obj",         label: "Perf vs Obj" },
  { key: "evolution_n1",     label: "Évolution N-1" },
  { key: "wasa_score",       label: "Wasa Score" },
];

const NIVEAUX = [
  { key: "excellent",   label: "Excellent",   color: "#16A34A", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300" },
  { key: "tres_bon",    label: "Très bon",    color: "#2563EB", bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-300" },
  { key: "moyen",       label: "Moyen",       color: "#F59E0B", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-300" },
  { key: "a_ameliorer", label: "À améliorer", color: "#EF4444", bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-300" },
];

function MS({ label, options, value, onChange, testid, labelMap, placeholder }) {
  return <FilterMultiSelect label={label} options={options} labelMap={labelMap} value={value||[]} onChange={onChange} testid={testid} size="md" topLabel topLabelClass="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1" className="flex-1 min-w-[130px]" placeholder={placeholder || "Tous"} menuWidth={220}/>;
}

function Sparkline({ data, color="#2563eb", width=100, height=24 }) {
  if (!data || data.length < 2) return <span className="text-slate-300 text-[10px]">—</span>;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v,i)=>`${(i*step).toFixed(1)},${(height-((v-min)/range)*height).toFixed(1)}`).join(" ");
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}><polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round"/></svg>;
}

function NiveauBadge({ niveau, label }) {
  const n = NIVEAUX.find(x => x.key === niveau) || NIVEAUX[3];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${n.bg} ${n.text} ${n.border}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{background:n.color}}/>
      {label || n.label}
    </span>
  );
}

function ScoreBadge({ score }) {
  const n = score >= 85 ? NIVEAUX[0] : score >= 70 ? NIVEAUX[1] : score >= 55 ? NIVEAUX[2] : NIVEAUX[3];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-[11px] tabular-nums ${n.bg} ${n.text} border ${n.border}`}>
      {score}<span className="text-[9px] opacity-70">/100</span>
    </span>
  );
}

function RankDelta({ delta }) {
  if (delta == null) return <Minus className="w-3 h-3 text-slate-300"/>;
  if (delta > 0) return <span className="flex items-center gap-0.5 text-emerald-600 font-bold text-[10px]"><ArrowUp className="w-3 h-3"/>{delta}</span>;
  if (delta < 0) return <span className="flex items-center gap-0.5 text-rose-600 font-bold text-[10px]"><ArrowDown className="w-3 h-3"/>{Math.abs(delta)}</span>;
  return <Minus className="w-3 h-3 text-slate-400"/>;
}

/* ─── Podium Card ─── */
function PodiumCard({ item, position }) {
  if (!item) return <div className="rounded-2xl border-2 border-dashed border-slate-200 h-52"/>;
  const styles = {
    1: { bg:"bg-gradient-to-br from-amber-400 via-orange-500 to-yellow-600", glow:"shadow-[0_0_60px_-5px_rgba(251,191,36,0.7)]", medal:"🥇", h:"h-60" },
    2: { bg:"bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-600", glow:"shadow-[0_0_40px_-5px_rgba(139,92,246,0.5)]", medal:"🥈", h:"h-52" },
    3: { bg:"bg-gradient-to-br from-blue-400 via-sky-500 to-cyan-600", glow:"shadow-[0_0_40px_-5px_rgba(59,130,246,0.5)]", medal:"🥉", h:"h-48" },
  };
  const s = styles[position];
  return (
    <div className={`relative ${s.h} rounded-2xl ${s.bg} ${s.glow} p-4 overflow-hidden text-white`} data-testid={`podium-${position}`}>
      {position === 1 && <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({length:18}).map((_,i)=>(
          <span key={i} className="absolute w-1.5 h-3 rounded-sm"
                style={{left:`${Math.random()*100}%`, top:`${Math.random()*100}%`,
                        background:["#fff","#fef3c7","#fed7aa"][i%3], opacity:0.6,
                        transform:`rotate(${Math.random()*360}deg)`}}/>
        ))}
      </div>}
      <div className="relative flex flex-col h-full">
        <div className="flex items-center justify-between">
          <span className="text-4xl">{s.medal}</span>
          <div className="flex items-center gap-1">
            {item.is_consolidated && <span className="text-[10px] font-bold bg-white/25 backdrop-blur px-2 py-0.5 rounded inline-flex items-center gap-1">🛰️ Consolidé</span>}
            <span className="text-[10px] font-bold uppercase bg-white/25 backdrop-blur px-2 py-0.5 rounded">#{position}</span>
          </div>
        </div>
        <div className="mt-auto">
          <div className="font-black text-lg leading-tight truncate drop-shadow">{item.name}</div>
          <div className="text-[10px] opacity-90 mt-0.5 truncate">
            {item.dr || item.region || "—"}{item.secteur ? ` · ${item.secteur}` : ""}
          </div>
          <div className="font-black text-xl mt-2 drop-shadow tabular-nums">{fmtEur(item.ca_realise)}</div>
          <div className="flex items-center gap-2 mt-1">
            <ScoreBadge score={item.wasa_score}/>
            <span className={`text-[11px] font-bold ${item.evolution_n1>=0?"text-emerald-100":"text-rose-100"}`}>
              {fmtPct(item.evolution_n1)}
            </span>
          </div>
          <div className="mt-2"><Sparkline data={item.sparkline} color="#fff" width={140} height={18}/></div>
        </div>
      </div>
    </div>
  );
}

/* ─── Stat Card ─── */
function StatCard({ icon:Icon, iconBg, label, value, sub, subTone, testid }) {
  const t = subTone==="up"?"text-emerald-600":subTone==="down"?"text-rose-600":"text-slate-500";
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded grid place-items-center ${iconBg}`}>
          <Icon className="w-3.5 h-3.5 text-white"/>
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex-1 truncate">{label}</div>
      </div>
      <div className="text-[20px] font-bold text-slate-900 tabular-nums leading-tight truncate">{value}</div>
      {sub && <div className={`text-[10px] font-semibold ${t}`}>{sub}</div>}
    </div>
  );
}

/* ═══ COMPOSANT PRINCIPAL ═══ */
export default function KpiRankingV2() {
  const [filtersMeta, setFiltersMeta] = useState({});
  const [f, setF] = useSharedKpiFilters({ regions:[], secteurs:[], corners:[], types:[], niveaux:[], annees:[2026], mois:[], mois_compare:[] }, { meta: filtersMeta.corners ? filtersMeta : null });
  const [scope, setScope] = useState("corner");
  const [metric, setMetric] = useState("ca_month");
  const [viewMode, setViewMode] = useState("individual"); // individual | consolidated
  const [data, setData] = useState({ items:[], cards:{}, levels:[] });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [tsMode, setTsMode] = useState("eur"); // eur | qte (approximation)
  const [showSatMgr, setShowSatMgr] = useState(false);
  const [satGroups, setSatGroups] = useState([]);
  const pageSize = 50;

  useEffect(() => { api.get("/kpi/filters").then(r => setFiltersMeta(r.data || {})).catch(()=>{}); }, []);
  const loadSatGroups = useCallback(() => {
    api.get("/satellite-groups").then(r => setSatGroups(r.data || [])).catch(()=>setSatGroups([]));
  }, []);
  useEffect(() => { loadSatGroups(); }, [loadSatGroups]);

  const reload = useCallback((alive = { ok: true }) => {
    setLoading(true);
    const p = { ...toParams(f), scope, metric, view: viewMode };
    if (f.niveaux?.length) p.niveau = f.niveaux.join(",");
    if (f.mois_compare?.[0]) p.mois_compare = f.mois_compare[0];
    api.get("/kpi/rankings", { params: p })
       .then(r => { if (alive.ok) setData(r.data || { items:[], cards:{}, levels:[] }); })
       .catch(()=>{ if (alive.ok) setData({items:[], cards:[], levels:[]}); })
       .finally(()=>{ if (alive.ok) setLoading(false); });
  }, [f, scope, metric, viewMode]);
  useEffect(()=>{ const alive = { ok: true }; reload(alive); return () => { alive.ok = false; }; }, [reload]);
  useEffect(()=>setPage(1), [f, scope, metric, viewMode]);

  const items = data.items || [];
  const cards = data.cards || {};
  const top3 = items.slice(0, 3);
  const top5Progression = useMemo(() => items.slice().sort((a,b)=>b.evolution_period-a.evolution_period).slice(0,5), [items]);
  const top5Score = useMemo(() => items.slice().sort((a,b)=>b.wasa_score-a.wasa_score).slice(0,5), [items]);
  const totalPages = Math.max(1, Math.ceil(items.length/pageSize));
  const rows = items.slice((page-1)*pageSize, page*pageSize);

  // Timeseries : top 3 sur 12 semaines (utilise sparkline)
  const tsData = useMemo(() => {
    if (top3.length === 0) return [];
    const len = Math.max(...top3.map(t=>t.sparkline?.length||0));
    return Array.from({length: len}, (_, i) => {
      const point = { label: `S${i+1}` };
      top3.forEach((t, idx) => { point[t.name] = t.sparkline?.[i] || 0; });
      return point;
    });
  }, [top3]);

  const doExport = () => {
    if (!items.length) { toast.error("Aucune donnée"); return; }
    const cols = ["Rang","Δ","Corner","DR","Secteur","CA €","Évol. période %","Évol. N-1 %","Wasa Score","Perf/Obj %","Niveau","Plans ouverts"];
    const lines = [cols.join(";")];
    items.forEach(r => lines.push([r.rank, r.rank_delta ?? "", r.name, r.dr||"", r.secteur||"",
      Math.round(r.ca_realise), r.evolution_period.toFixed(1).replace(".",","),
      r.evolution_n1.toFixed(1).replace(".",","), r.wasa_score, r.perf_obj.toFixed(1).replace(".",","),
      r.niveau_label, r.plans_ouverts].join(";")));
    const blob = new Blob(["\ufeff"+lines.join("\n")], {type:"text/csv;charset=utf-8"});
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "ranking.csv"; a.click();
    toast.success("Export téléchargé");
  };

  return (
    <div className="space-y-3" data-testid="ranking-v2">
      {/* Filtres — Ligne 1 */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-2" data-testid="rank-filter-l1">
        <MS label="Structure (Région)" testid="rank-f-region" options={filtersMeta.regions||[]}
            value={f.regions} onChange={v=>setF(p=>({...p, regions:v}))}/>
        <MS label="Secteur" testid="rank-f-secteur" options={filtersMeta.secteurs||[]}
            value={f.secteurs} onChange={v=>setF(p=>({...p, secteurs:v}))}/>
        <MS label="Corner" testid="rank-f-corner" options={(filtersMeta.corners||[]).map(c=>c.corner||c.corner_key||c).filter(Boolean).slice(0,300)}
            value={f.corners} onChange={v=>setF(p=>({...p, corners:v}))}/>
        <MS label="Type" testid="rank-f-type" options={filtersMeta.types||[]}
            value={f.types} onChange={v=>setF(p=>({...p, types:v}))}/>
        <MS label="Niveau" testid="rank-f-niveau" placeholder="Tous niveaux"
            options={NIVEAUX.map(n=>n.key)} labelMap={Object.fromEntries(NIVEAUX.map(n=>[n.key,n.label]))}
            value={f.niveaux} onChange={v=>setF(p=>({...p, niveaux:v}))}/>
        <button onClick={()=>setF({regions:[],secteurs:[],corners:[],types:[],niveaux:[],annees:[2026],mois:[],mois_compare:[]})}
                data-testid="rank-f-reset"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700">
          <RotateCcw className="w-3.5 h-3.5"/>Réinitialiser
        </button>
        <button onClick={()=>setShowSatMgr(true)} data-testid="rank-sat-open"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-200 bg-violet-50 text-[12px] font-semibold text-violet-700 hover:bg-violet-100">
          <Satellite className="w-3.5 h-3.5"/>Associer des corners
          {satGroups.length > 0 && <span className="tabular-nums text-[10px] font-bold bg-violet-600 text-white rounded-full px-1.5">{satGroups.length}</span>}
        </button>
      </div>

      {/* Filtres — Ligne 2 (période) */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-2" data-testid="rank-filter-l2">
        <div className="flex items-end gap-2">
          <MS label="Année" testid="rank-f-annee" options={(filtersMeta.annees||[2025,2026]).map(String)}
              value={(f.annees||[]).map(String)} onChange={v=>setF(p=>({...p, annees:v.map(Number)}))}/>
          <MS label="Mois" testid="rank-f-mois" options={filtersMeta.mois||MONTHS_FR}
              value={f.mois} onChange={v=>setF(p=>({...p, mois:v}))}/>
          <CustomDates testid="rank-f-dates" value={f} onChange={p=>setF(o=>({...o, ...p}))} />
        </div>
        <div className="flex items-end gap-2 ml-4">
          <span className="text-[10px] font-semibold text-slate-500 pb-2">Comparé à :</span>
          <MS label="Mois de référence" testid="rank-f-cmp" options={filtersMeta.mois||MONTHS_FR}
              value={f.mois_compare} onChange={v=>setF(p=>({...p, mois_compare:v.slice(0,1)}))}/>
        </div>
      </div>

      {/* Scope + Metric */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Classement par</span>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="rank-scope">
          {SCOPES.map(s=>(
            <button key={s.key} data-testid={`rank-scope-${s.key}`} onClick={()=>setScope(s.key)}
                    className={`px-3 py-1.5 rounded text-[11px] font-bold ${scope===s.key?"bg-slate-900 text-white":"text-slate-600 hover:bg-slate-50"}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="inline-flex gap-1 flex-wrap ml-4" data-testid="rank-metric">
          {METRICS.map(m=>(
            <button key={m.key} data-testid={`rank-metric-${m.key}`} onClick={()=>setMetric(m.key)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${metric===m.key?"bg-blue-600 text-white border-blue-600":"bg-white text-slate-600 border-slate-200"}`}>
              {m.label}
            </button>
          ))}
        </div>
        {scope === "corner" && satGroups.length > 0 && (
          <div className="inline-flex rounded-lg border border-violet-200 bg-white p-0.5 ml-auto" data-testid="rank-view-toggle">
            <button data-testid="rank-view-individual" onClick={()=>setViewMode("individual")}
                    className={`px-3 py-1.5 rounded text-[11px] font-bold ${viewMode==="individual"?"bg-slate-900 text-white":"text-slate-600 hover:bg-slate-50"}`}>
              Vue individuelle
            </button>
            <button data-testid="rank-view-consolidated" onClick={()=>setViewMode("consolidated")}
                    className={`px-3 py-1.5 rounded text-[11px] font-bold inline-flex items-center gap-1 ${viewMode==="consolidated"?"bg-violet-600 text-white":"text-slate-600 hover:bg-slate-50"}`}>
              <Satellite className="w-3 h-3"/> Vue consolidée
            </button>
          </div>
        )}
      </div>

      {/* Podium */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="pt-6"><PodiumCard item={top3[1]} position={2}/></div>
        <div><PodiumCard item={top3[0]} position={1}/></div>
        <div className="pt-10"><PodiumCard item={top3[2]} position={3}/></div>
      </div>

      {/* 6 Cards synthèse */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <StatCard testid="c-total" icon={Trophy} iconBg="bg-slate-800" label="Corners classés"
                  value={fmtInt(cards.n_total)} sub={cards.delta_total?`${cards.delta_total>=0?"+":""}${cards.delta_total} vs préc.`:"—"} subTone={cards.delta_total>=0?"up":"down"}/>
        <StatCard testid="c-best" icon={Award} iconBg="bg-amber-500" label="Meilleur CA"
                  value={cards.best_name || "—"} sub={fmtEur(cards.best_value)}/>
        <StatCard testid="c-moy" icon={TrendingUp} iconBg="bg-blue-600" label="CA moyen"
                  value={fmtEur(cards.ca_moyen)}
                  sub={cards.ca_moyen_evol_pct?`${fmtPct(cards.ca_moyen_evol_pct)} vs comparé`:"—"}
                  subTone={cards.ca_moyen_evol_pct>=0?"up":"down"}/>
        <StatCard testid="c-evo" icon={TrendingUp} iconBg="bg-emerald-600" label="Évolution moy."
                  value={fmtPct(cards.evolution_moyenne_pct)}
                  sub="vs période comparée"
                  subTone={cards.evolution_moyenne_pct>=0?"up":"down"}/>
        <StatCard testid="c-score" icon={Award} iconBg="bg-violet-600" label="Wasa Score moyen"
                  value={`${Math.round(cards.score_moyen||0)}/100`} sub={`${data.total||0} évaluations`}/>
        <StatCard testid="c-prog" icon={TrendingUp} iconBg="bg-orange-500" label="Progressions fortes"
                  value={fmtInt(cards.n_progressions_fortes)} sub="corners > +15%"/>
      </div>

      {/* Bloc central 3 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Top 5 Progression */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="top5-prog">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-800">Top 5 Progressions</h3>
            <span className="text-[10px] text-slate-400">vs période comparée</span>
          </div>
          <div className="space-y-2">
            {top5Progression.map((r,i)=>(
              <div key={r.name} className="flex items-center gap-2 text-[12px]">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-[10px]">{i+1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{r.dr || "—"} · {r.secteur || "—"}</div>
                </div>
                <span className="tabular-nums font-bold text-emerald-600">{fmtPct(r.evolution_period)}</span>
              </div>
            ))}
            {top5Progression.length===0 && <div className="text-center text-[11px] italic text-slate-400 py-4">Aucune donnée</div>}
          </div>
        </div>

        {/* Timeseries */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ts-chart">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-800">Évolution top 3</h3>
            <div className="inline-flex border border-slate-200 rounded overflow-hidden">
              <button onClick={()=>setTsMode("eur")} className={`px-2 py-0.5 text-[10px] font-semibold ${tsMode==="eur"?"bg-blue-600 text-white":"bg-white"}`}>€</button>
              <button onClick={()=>setTsMode("qte")} className={`px-2 py-0.5 text-[10px] font-semibold ${tsMode==="qte"?"bg-blue-600 text-white":"bg-white"}`}>Unités</button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={tsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
              <XAxis dataKey="label" tick={{fontSize:9}} axisLine={false}/>
              <YAxis tick={{fontSize:9}} tickFormatter={v=>tsMode==="eur"?Math.round(v/1000)+"k":fmtInt(v)}/>
              <Tooltip formatter={v=>tsMode==="eur"?fmtEur(v):fmtInt(v)}/>
              <Legend wrapperStyle={{fontSize:10}}/>
              {top3.map((t,i)=><Line key={t.name} type="monotone" dataKey={t.name} stroke={["#F59E0B","#8B5CF6","#3B82F6"][i]} strokeWidth={2} dot={{r:2}}/>)}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top 5 Wasa Score */}
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="top5-score">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-800">Top 5 Wasa Score</h3>
            <span className="text-[10px] text-slate-400">/100</span>
          </div>
          <div className="space-y-2">
            {top5Score.map((r,i)=>(
              <div key={r.name} className="flex items-center gap-2 text-[12px]">
                <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 grid place-items-center font-bold text-[10px]">{i+1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{r.dr || "—"} · {r.secteur || "—"}</div>
                </div>
                <ScoreBadge score={r.wasa_score}/>
              </div>
            ))}
            {top5Score.length===0 && <div className="text-center text-[11px] italic text-slate-400 py-4">Aucune donnée</div>}
          </div>
        </div>
      </div>

      {/* Tableau Classement Complet */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <h3 className="text-[13px] font-bold text-slate-800">Classement complet</h3>
          <span className="text-[11px] text-slate-500 ml-2">{loading?"Chargement…":`${items.length} lignes`}</span>
          <button onClick={doExport} data-testid="rank-export"
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-200 text-[11px] font-semibold text-slate-700 hover:border-slate-300">
            <Download className="w-3 h-3"/>Exporter
          </button>
        </div>
        <div className="overflow-x-auto max-h-[560px]">
          <table className="min-w-[1400px] w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
                <th className="px-3 py-2 w-[45px]">Rang</th>
                <th className="px-3 py-2 w-[40px]">Δ</th>
                <th className="px-3 py-2 min-w-[200px]">Corner</th>
                <th className="px-3 py-2 min-w-[140px]">DR · Secteur</th>
                <th className="px-3 py-2 text-right min-w-[110px]">CA Mensuel €</th>
                <th className="px-3 py-2 text-right min-w-[100px]">Évol. cmp</th>
                <th className="px-3 py-2 text-right min-w-[90px]">Évol. N-1</th>
                <th className="px-3 py-2 min-w-[90px]">Wasa Score</th>
                <th className="px-3 py-2 text-right min-w-[90px]">Perf/Obj</th>
                <th className="px-3 py-2 min-w-[110px]">Tendance 12s</th>
                <th className="px-3 py-2 min-w-[110px]">Niveau</th>
                <th className="px-3 py-2 text-right min-w-[80px]">Plans</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.name} data-testid={`rank-row-${r.rank}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2 font-bold tabular-nums text-slate-700">{r.rank}</td>
                  <td className="px-3 py-2"><RankDelta delta={r.rank_delta}/></td>
                  <td className="px-3 py-2 font-semibold text-slate-900">
                    <div className="flex items-center gap-1.5">
                      {r.is_consolidated && <Satellite className="w-3 h-3 text-violet-600 shrink-0" title={`Consolidé : ${(r.members||[]).join(" + ")}`}/>}
                      <span className="truncate">{r.name}</span>
                    </div>
                    {r.is_consolidated && <div className="text-[9px] text-violet-600 mt-0.5 truncate">🛰️ {(r.members||[]).length} corners</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-600 truncate">{r.dr || "—"} · {r.secteur || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtEur(r.ca_realise)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.evolution_period>=0?"text-emerald-600":"text-rose-600"}`}>{fmtPct(r.evolution_period)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.evolution_n1>=0?"text-emerald-600":"text-rose-600"}`}>{fmtPct(r.evolution_n1)}</td>
                  <td className="px-3 py-2"><ScoreBadge score={r.wasa_score}/></td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.perf_obj>=100?"text-emerald-600":"text-slate-600"}`}>{r.perf_obj.toFixed(0)}%</td>
                  <td className="px-3 py-2"><Sparkline data={r.sparkline} color={r.evolution_n1>=0?"#10B981":"#EF4444"} width={90} height={20}/></td>
                  <td className="px-3 py-2"><NiveauBadge niveau={r.niveau} label={r.niveau_label}/></td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.plans_ouverts>0 ? <span className="text-orange-600">{r.plans_ouverts}</span> : "—"}</td>
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={12} className="px-4 py-8 text-center italic text-slate-400">{loading?"Chargement…":"Aucun classement pour ces filtres."}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center flex-wrap gap-3">
          {/* Légende */}
          <div className="flex items-center gap-3 text-[10px] flex-wrap">
            {NIVEAUX.map(n=>(
              <div key={n.key} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{background:n.color}}/>
                <span className="text-slate-600">
                  {n.label}
                  {n.key==="excellent" && " (≥85)"}
                  {n.key==="tres_bon" && " (70-84)"}
                  {n.key==="moyen" && " (55-69)"}
                  {n.key==="a_ameliorer" && " (<55)"}
                </span>
              </div>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-slate-500 tabular-nums">
              {items.length ? `${(page-1)*pageSize+1}-${Math.min(page*pageSize, items.length)} sur ${fmtInt(items.length)}` : ""}
            </span>
            <button data-testid="rank-page-prev" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}
                    className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5"/></button>
            <span className="text-[11px] font-semibold text-slate-700 tabular-nums px-2">Page {page}/{totalPages}</span>
            <button data-testid="rank-page-next" disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}
                    className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5"/></button>
          </div>
        </div>
      </div>
      {showSatMgr && <SatelliteManager
        open={showSatMgr}
        onClose={()=>setShowSatMgr(false)}
        groups={satGroups}
        cornerOptions={(filtersMeta.corners||[]).map(c=>c.corner||c.corner_key||c).filter(Boolean)}
        onChange={loadSatGroups}
      />}
    </div>
  );
}

/* ─── Satellite Manager Sheet ─── */
function SatelliteManager({ open, onClose, groups, cornerOptions, onChange }) {
  const [editing, setEditing] = useState(null);
  const empty = { name: "", main_corner: "", members: [] };
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (g) => { setForm({ name: g.name, main_corner: g.main_corner, members: g.members||[], id: g.id }); setEditing(g); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async () => {
    if (!form.name.trim() || !form.main_corner || (form.members||[]).length === 0) {
      toast.error("Nom, corner principal et au moins 1 membre requis"); return;
    }
    try {
      if (form.id) {
        await api.put(`/satellite-groups/${form.id}`, { name: form.name, main_corner: form.main_corner, members: form.members });
        toast.success("Groupe mis à jour");
      } else {
        await api.post(`/satellite-groups`, { name: form.name, main_corner: form.main_corner, members: form.members });
        toast.success("Groupe créé");
      }
      onChange?.(); cancel();
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Supprimer le groupe "${g.name}" ?`)) return;
    try { await api.delete(`/satellite-groups/${g.id}`); toast.success("Groupe supprimé"); onChange?.(); }
    catch (e) { toast.error("Erreur suppression"); }
  };

  const filteredCorners = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return qq ? cornerOptions.filter(c => c.toLowerCase().includes(qq)) : cornerOptions.slice(0, 200);
  }, [cornerOptions, q]);
  const toggleMember = (c) => setForm(s => ({ ...s, members: s.members.includes(c) ? s.members.filter(x=>x!==c) : [...s.members, c] }));

  return (
    <>
      <div className="fixed inset-0 z-50 bg-slate-900/50" onClick={onClose}/>
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl flex flex-col" data-testid="sat-manager">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-violet-50 to-white">
          <Satellite className="w-5 h-5 text-violet-600"/>
          <div className="flex-1">
            <div className="font-bold text-slate-900">Vitrines satellites</div>
            <div className="text-[11px] text-slate-500">Associer plusieurs corners pour les additionner dans le classement</div>
          </div>
          <button onClick={onClose} data-testid="sat-close" className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {editing === null && (
            <>
              <button onClick={startNew} data-testid="sat-new"
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                <Plus className="w-4 h-4"/> Nouveau groupe
              </button>
              <div className="space-y-2">
                {groups.length === 0 && <div className="text-center text-sm italic text-slate-400 py-8">Aucun groupe. Créez-en un.</div>}
                {groups.map(g => (
                  <div key={g.id} data-testid={`sat-group-${g.id}`} className="border border-slate-200 rounded-lg p-3 bg-white">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
                          <Satellite className="w-3.5 h-3.5 text-violet-600"/>
                          {g.name}
                          {g.is_default && <span className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">défaut</span>}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Principal : <span className="font-semibold text-slate-700">{g.main_corner}</span></div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {(g.members||[]).map(m => (
                            <span key={m} className={`text-[10px] px-2 py-0.5 rounded-full ${m===g.main_corner?"bg-violet-100 text-violet-700 font-semibold":"bg-slate-100 text-slate-600"}`}>{m}</span>
                          ))}
                        </div>
                      </div>
                      <button onClick={()=>startEdit(g)} data-testid={`sat-edit-${g.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"><Pencil className="w-3.5 h-3.5"/></button>
                      <button onClick={()=>remove(g)} data-testid={`sat-del-${g.id}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {editing !== null && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button onClick={cancel} className="text-[12px] font-semibold text-slate-500 hover:text-slate-800">← Retour</button>
                <div className="text-sm font-bold text-slate-900">{form.id ? "Modifier le groupe" : "Nouveau groupe"}</div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Nom du groupe</label>
                <input data-testid="sat-form-name" value={form.name} onChange={e=>setForm(s=>({...s, name:e.target.value}))}
                       className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="ex : Marseille Saint Jean"/>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Corner principal</label>
                <SearchSelect value={form.main_corner} onChange={v => setForm(s=>({...s, main_corner:v, members: s.members.includes(v)?s.members:[...s.members, v] }))} options={[...cornerOptions.map(c => ({ value: c, label: c }))]} placeholder={"— Sélectionner —"} data-testid="sat-form-main" size="lg" menuWidth={260}/>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Membres du groupe · {form.members.length} sélectionné(s)</label>
                <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Rechercher un corner…"
                       className="w-full mb-2 px-3 py-1.5 border border-slate-200 rounded-lg text-[12px]"/>
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {filteredCorners.map(c => {
                    const on = form.members.includes(c);
                    const isMain = c === form.main_corner;
                    return (
                      <button key={c} onClick={()=>toggleMember(c)} disabled={isMain}
                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] ${on?"bg-violet-50":""} ${isMain?"opacity-60 cursor-not-allowed":"hover:bg-slate-50"}`}>
                        <div className={`w-3.5 h-3.5 rounded border-2 grid place-items-center ${on?"bg-violet-600 border-violet-600":"border-slate-300"}`}>
                          {on && <div className="w-1.5 h-1.5 bg-white rounded-[1px]"/>}
                        </div>
                        <span className="flex-1 truncate">{c}</span>
                        {isMain && <span className="text-[9px] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">principal</span>}
                      </button>
                    );
                  })}
                  {filteredCorners.length === 0 && <div className="p-4 text-center italic text-slate-400 text-[11px]">Aucun corner</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button onClick={save} data-testid="sat-save" className="flex-1 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                  {form.id ? "Enregistrer" : "Créer le groupe"}
                </button>
                <button onClick={cancel} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700">Annuler</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
